import { useFieldArray, useFormContext } from 'react-hook-form';

import CheckboxInput from '@/components/form/CheckboxInput.tsx';
import SingleSelectInput from '@/components/form/SingleSelectInput.tsx';
import { currencySelectOptions } from '@/features/dashboard/paymentConfig/types/Currency.utils.ts';
import { ValueInput } from '@/components/form/ValueInput.tsx';
import TwoInputsRow from '@/components/form/ui/TwoInputsRow.tsx';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card.tsx';
import AgeRestrictionForm from './AgeRestrictionForm.tsx';
import { BasePriceInputForm } from '@/features/dashboard/tourConfig/schemas/tourPricesSchema.ts';
import { tourConfigCostsRoute } from '@/features/dashboard/tourConfig/routes/tourConfigRoutes.ts';

const TourBaseCostConfigCard = () => {
  const { tourId } = tourConfigCostsRoute.useParams();
  if (!tourId) {
    throw new Error('Tour ID is required');
  }

  const {
    control,
    register,
    watch,
    formState: { errors },
  } = useFormContext<BasePriceInputForm>();

  const currencyValue = watch('currency');
  const isVariantOnlyForDefinedAgeRanges = watch(
    'isVariantOnlyForDefinedAgeRanges',
  );
  const isPriceByAgeValue = watch('isPriceByAge');

  const ageRestrictionPricesFieldArray = useFieldArray({
    control,
    name: 'ageRestrictionPrices',
  });

  const handleIsPriceByAgeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    if (!checked) {
      ageRestrictionPricesFieldArray.replace([]);
    } else if (ageRestrictionPricesFieldArray.fields.length === 0) {
      ageRestrictionPricesFieldArray.append({
        minAge: '',
        maxAge: '',
        price: '',
      });
    }
  };

  return (
    <Card>
      <CardHeader className="border-b p-4">
        <CardTitle>Koszt podstawowy</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <SingleSelectInput
            fieldName="currency"
            placeholder="Wybierz"
            label="Waluta"
            required
            control={control}
            options={currencySelectOptions}
            error={errors.currency?.message}
            className="max-w-[120px]"
          />
          {!isPriceByAgeValue && (
            <div className="max-w-[200px]">
              <ValueInput
                label="Cena podstawowa"
                register={register('price', {
                  valueAsNumber: true,
                })}
                type="number"
                step={0.01}
                placeholder="0"
                sufix={currencyValue}
                error={errors.price?.message}
                required
              />
            </div>
          )}
        </div>

        <TwoInputsRow ratio="1/1">
          <CheckboxInput
            label="Cena zależna od wieku"
            register={register('isPriceByAge')}
            error={errors.isPriceByAge?.message}
            onChange={handleIsPriceByAgeChange}
          />
          {isPriceByAgeValue && (
            <CheckboxInput
              label="Dostępne tylko dla wybranych grup wiekowych"
              register={register('isVariantOnlyForDefinedAgeRanges')}
              error={errors.isVariantOnlyForDefinedAgeRanges?.message}
            />
          )}
        </TwoInputsRow>
      </CardContent>
      {isPriceByAgeValue && (
        <AgeRestrictionForm
          currency={currencyValue}
          fieldArray={ageRestrictionPricesFieldArray}
          isVariantOnlyForDefinedAgeRanges={isVariantOnlyForDefinedAgeRanges}
        />
      )}
    </Card>
  );
};

export default TourBaseCostConfigCard;
